/**
 * Shared labour cost calculations for job costing.
 *
 * Hours: net_work_seconds / 3600 when net_work_seconds > 0, else total_hours.
 * Line cost: hours × time_logs.hourly_rate (snapshot).
 */

export function normalizeLogHours(log) {
  const netSeconds = Number(log?.net_work_seconds);

  if (Number.isFinite(netSeconds) && netSeconds > 0) {
    return netSeconds / 3600;
  }

  const hours = Number(log?.total_hours);
  return Number.isFinite(hours) ? hours : 0;
}

export function isRateMissing(log) {
  const rate = log?.hourly_rate;

  if (rate === null || rate === undefined || rate === "") {
    return true;
  }

  const parsed = Number(rate);
  if (!Number.isFinite(parsed)) return true;

  return parsed === 0;
}

export function computeLineLabourCost(log) {
  const hours = normalizeLogHours(log);
  const rate = Number(log?.hourly_rate);
  if (!Number.isFinite(hours) || !Number.isFinite(rate)) return 0;
  return hours * rate;
}

export function computeTotalLabourCost(logs = []) {
  return logs.reduce((sum, log) => sum + computeLineLabourCost(log), 0);
}

export function groupLabourByEmployee(logs = []) {
  const groupsMap = {};

  logs.forEach((log) => {
    const employeeKey =
      log.supabase_user_id || log.employee || "unknown";
    const hours = normalizeLogHours(log);
    const lineCost = computeLineLabourCost(log);

    if (!groupsMap[employeeKey]) {
      groupsMap[employeeKey] = {
        employeeKey,
        employeeName: log.employee || "Unknown Employee",
        totalHours: 0,
        labourCost: 0,
        lines: [],
      };
    }

    groupsMap[employeeKey].totalHours += hours;
    groupsMap[employeeKey].labourCost += lineCost;
    groupsMap[employeeKey].lines.push({
      id: log.id,
      dateIso: log.date_iso,
      hours,
      hourlyRate: Number(log.hourly_rate || 0),
      rateMissing: isRateMissing(log),
      lineCost,
    });
  });

  return Object.values(groupsMap)
    .map((group) => ({
      ...group,
      effectiveRate:
        group.totalHours > 0 ? group.labourCost / group.totalHours : 0,
      lines: group.lines.sort((a, b) => {
        if (!a.dateIso && !b.dateIso) return 0;
        if (!a.dateIso) return 1;
        if (!b.dateIso) return -1;
        return b.dateIso.localeCompare(a.dateIso);
      }),
    }))
    .sort((a, b) => b.labourCost - a.labourCost);
}

export function computeLabourTotals(logs = []) {
  const groups = groupLabourByEmployee(logs);
  const totalHours = groups.reduce(
    (sum, group) => sum + group.totalHours,
    0,
  );

  return {
    totalHours,
    labourCost: computeTotalLabourCost(logs),
    groups,
  };
}

export function formatLineRateDisplay(line) {
  if (line.rateMissing) {
    return "Rate Missing";
  }

  return `$${line.hourlyRate.toFixed(2)}/hr`;
}

export function formatEffectiveRateDisplay(group) {
  if (group.totalHours <= 0) {
    return "—";
  }

  const allRatesMissing =
    group.lines.length > 0 &&
    group.lines.every((line) => line.rateMissing);

  if (allRatesMissing) {
    return "Rate Missing";
  }

  return `$${group.effectiveRate.toFixed(2)}/hr`;
}
